import api from './api'
import { fetchAuthSession } from 'aws-amplify/auth'

export const usersService = {
  // List users for a tenant (tenant admin, super admin, platform admin) - with pagination and search
  async listUsers(tenantId = null, { cursor, limit = 20, search = '', includeInactive = false, sortBy, sortOrder } = {}) {
    try {
      const params = new URLSearchParams()
      if (tenantId) params.append('tenantId', tenantId)
      if (cursor) params.append('cursor', cursor)
      if (search) params.append('search', search)
      if (includeInactive) params.append('includeInactive', 'true')
      params.append('limit', limit.toString())
      if (sortBy) params.append('sortBy', sortBy)
      if (sortOrder) params.append('sortOrder', sortOrder)

      const response = await api.get(`/users?${params.toString()}`)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Invite a new user (tenant admin, super admin, platform admin).
  // `expiresAt` (optional) is an admin-picked YYYY-MM-DD calendar date that
  // sets a membership expiry at invite time; omit it (or pass null/'') for
  // permanent access. (#288)
  async inviteUser(email, role = 'user', tenantId = null, firstName = null, lastName = null, expiresAt = null) {
    try {
      const payload = { email: email?.toLowerCase().trim(), role }
      if (tenantId) {
        payload.tenantId = tenantId
      }
      if (firstName) {
        payload.firstName = firstName
      }
      if (lastName) {
        payload.lastName = lastName
      }
      // Only include expiresAt when a date was actually supplied. Empty string
      // / null / undefined all mean "no expiry" and the key is omitted.
      if (expiresAt) {
        payload.expiresAt = expiresAt
      }
      const response = await api.post('/users', payload)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Update a user (role, status, and optionally expiresAt).
  //
  // Expiry is tri-state on the API (#288):
  //   - `expiresAt: 'YYYY-MM-DD'` → set/extend the membership expiry
  //   - `expiresAt: null`        → remove the expiry (permanent access)
  //   - key omitted               → leave expiry unchanged
  // `updates` is passed straight through, so an explicit `null` is preserved
  // (JSON-serialized as null) rather than dropped.
  async updateUser(userId, updates) {
    try {
      const response = await api.put(`/users/${userId}`, updates)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Admin-set a temporary password for a tenant user (#80). Pass a password to set
  // a specific one, or omit it to have the server generate one (returned in the
  // response as temporaryPassword). The user is forced to change it at next login.
  async adminResetPassword(userId, password = null) {
    const body = password ? { password } : {}
    const response = await api.post(`/users/${userId}/reset-password`, body)
    return response.data
  },

  // Admin-set a temporary password for a platform user (super admin only) (#80).
  async resetPlatformUserPassword(userId, password = null) {
    const body = password ? { password } : {}
    const response = await api.post(`/platform/users/${userId}/reset-password`, body)
    return response.data
  },

  // Delete a user (tenant admin, super admin, platform admin)
  async deleteUser(userId) {
    try {
      const response = await api.delete(`/users/${userId}`)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // List platform users (super admin, platform admin) - with pagination, search, and filter
  async listPlatformUsers({ cursor, limit = 20, search = '', includeInactive = false, sortBy, sortOrder } = {}) {
    try {
      const params = new URLSearchParams()
      if (cursor) params.append('cursor', cursor)
      if (search) params.append('search', search)
      if (includeInactive) params.append('includeInactive', 'true')
      params.append('limit', limit.toString())
      if (sortBy) params.append('sortBy', sortBy)
      if (sortOrder) params.append('sortOrder', sortOrder)

      const response = await api.get(`/platform/users?${params.toString()}`)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Invite a platform user (super admin only)
  async invitePlatformUser(email, role) {
    try {
      const response = await api.post('/platform/users', { email: email?.toLowerCase().trim(), role })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Update a platform user (super admin only)
  async updatePlatformUser(userId, updates) {
    try {
      const response = await api.put(`/platform/users/${userId}`, updates)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Resend invitation to a user (tenant admin, super admin, platform admin)
  async resendInvitation(userId) {
    try {
      const response = await api.post(`/users/${userId}/resend-invitation`)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Resend invitation to a platform user (super admin only)
  async resendPlatformInvitation(userId) {
    try {
      const response = await api.post(`/platform/users/${userId}/resend-invitation`)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Bulk status change (activate/deactivate multiple users)
  async bulkStatus(action, payload, tenantId = null) {
    try {
      const url = tenantId ? `/users/bulk-status?tenantId=${tenantId}` : '/users/bulk-status'
      const response = await api.post(url, { action, ...payload })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // MFA Methods

  // Helper to get access token for Cognito MFA operations
  async _getAccessToken() {
    const session = await fetchAuthSession()
    const accessToken = session.tokens?.accessToken
    if (!accessToken) {
      throw new Error('No access token available')
    }
    return accessToken.toString()
  },

  // Get MFA status for the current user
  async getMfaStatus() {
    try {
      const response = await api.get('/users/me/mfa')
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Start MFA setup - get TOTP secret and QR code
  // Requires access token for Cognito's AssociateSoftwareToken
  async setupMfa() {
    try {
      const accessToken = await this._getAccessToken()
      const response = await api.get('/users/me/mfa/setup', {
        headers: { 'X-Access-Token': accessToken }
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Verify TOTP code and enable MFA
  // Requires access token for Cognito's VerifySoftwareToken
  async verifyMfa(code) {
    try {
      const accessToken = await this._getAccessToken()
      const response = await api.post('/users/me/mfa/verify', { code }, {
        headers: { 'X-Access-Token': accessToken }
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Disable MFA
  // Requires access token for Cognito's SetUserMFAPreference
  async disableMfa(code) {
    try {
      const accessToken = await this._getAccessToken()
      const response = await api.delete('/users/me/mfa', {
        data: { code },
        headers: { 'X-Access-Token': accessToken }
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Regenerate recovery codes
  // Requires access token for Cognito's VerifySoftwareToken
  async regenerateRecoveryCodes(code) {
    try {
      const accessToken = await this._getAccessToken()
      const response = await api.post('/users/me/mfa/recovery-codes', { code }, {
        headers: { 'X-Access-Token': accessToken }
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Update user preferences (language, etc.)
  async updatePreferences(preferences) {
    try {
      const response = await api.patch('/users/me/preferences', preferences)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Request a change to the current user's sign-in email. The new address must
  // be verified via a tokenized link before it takes effect. (#312)
  async requestEmailChange(newEmail) {
    try {
      const response = await api.post('/users/me/email-change', {
        newEmail: newEmail?.toLowerCase().trim(),
      })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Cancel a pending email-change request for the current user. (#312)
  async cancelEmailChange() {
    try {
      const response = await api.delete('/users/me/email-change')
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Clear email status for a user (admin only - for soft bounces)
  async clearEmailStatus(userId) {
    try {
      const response = await api.delete(`/users/${userId}/email-status`)
      return response.data
    } catch (error) {
      throw error
    }
  },
}

// Named exports for direct imports
export const listUsers = (tenantId = null, options = {}) => usersService.listUsers(tenantId, options)
export const inviteUser = (email, role, tenantId, firstName, lastName, expiresAt) => usersService.inviteUser(email, role, tenantId, firstName, lastName, expiresAt)
export const updateUser = (userId, updates) => usersService.updateUser(userId, updates)
export const deleteUser = (userId) => usersService.deleteUser(userId)
export const listPlatformUsers = (options = {}) => usersService.listPlatformUsers(options)
export const invitePlatformUser = (email, role) => usersService.invitePlatformUser(email, role)
export const updatePlatformUser = (userId, updates) => usersService.updatePlatformUser(userId, updates)
export const adminResetPassword = (userId, password) => usersService.adminResetPassword(userId, password)
export const resetPlatformUserPassword = (userId, password) => usersService.resetPlatformUserPassword(userId, password)
export const resendInvitation = (userId) => usersService.resendInvitation(userId)
export const resendPlatformInvitation = (userId) => usersService.resendPlatformInvitation(userId)
export const bulkStatus = (action, payload, tenantId) => usersService.bulkStatus(action, payload, tenantId)
export const getMfaStatus = () => usersService.getMfaStatus()
export const setupMfa = () => usersService.setupMfa()
export const verifyMfa = (code) => usersService.verifyMfa(code)
export const disableMfa = (code) => usersService.disableMfa(code)
export const regenerateRecoveryCodes = (code) => usersService.regenerateRecoveryCodes(code)
export const updatePreferences = (preferences) => usersService.updatePreferences(preferences)
export const clearEmailStatus = (userId) => usersService.clearEmailStatus(userId)
export const requestEmailChange = (newEmail) => usersService.requestEmailChange(newEmail)
export const cancelEmailChange = () => usersService.cancelEmailChange()

export default usersService
